import { AnalyticsBrowser } from "@segment/analytics-next";
import { useRouter, defineNuxtPlugin, nextTick } from "#imports";
import { NavigationFailureType } from "vue-router";
import { useSegment } from "./composables/useSegment.mjs";
let segment;
let debugEnabled = false;
let trackOnNextTick = false;
let deriveAdditionalEventData = () => ({});
export default defineNuxtPlugin((nuxt) => {
  const config = nuxt.$config.public.segment;
  debugEnabled = config.debugEnabled ?? false;
  trackOnNextTick = config.trackOnNextTick ?? false;
  deriveAdditionalEventData = config.vueRouterAdditionalEventData || deriveAdditionalEventData;
  if (!process.client) {
    return {};
  }
  if (!config.settings.writeKey) {
    console.log(`[segment]: settings.writeKey is empty - cannot initialise segment plugin`);
    return {};
  }
  const settings = config.settings;
  const options = config.options;
  let integrations = {};
  if (options?.apiHost || options?.protocol) {
    integrations = {
      integrations: {
        "Segment.io": {
          apiHost: options.apiHost,
          protocol: options.protocol || "https"
        }
      }
    };
  }
  segment = AnalyticsBrowser.load(settings, integrations);
  if (config.enableRouterSync) {
    initVueRouterTracking(config.ignoredViews);
  }
  return {
    provide: {
      segment
    }
  };
});
function isNavigationFailure(failure, navigationFailureType) {
  if (!(failure instanceof Error)) {
    return false;
  }
  return !!(failure.type & navigationFailureType);
}
function fullUrlFromRoute(route, baseUrl = "") {
  let fullUrl = baseUrl;
  if (!fullUrl.endsWith("/")) {
    fullUrl += "/";
  }
  fullUrl += route.fullPath.startsWith("/") ? route.fullPath.substring(1) : route.fullPath;
  return fullUrl;
}
function isIgnoredRoute(ignoredViews, to, from) {
  if (!to.name) {
    return typeof ignoredViews === "function" && ignoredViews(to, from);
  }
  if (typeof to.name !== "string") {
    return false;
  }
  const isIgnoredView = Array.isArray(ignoredViews) && ignoredViews.includes(to.name);
  return typeof ignoredViews === "function" ? ignoredViews(to, from) : isIgnoredView;
}
async function initVueRouterTracking(ignoredViews) {
  const router = useRouter();
  const baseUrl = router.options?.history?.base ?? "";
  router.afterEach(async (to, from, failure) => {
    if (isIgnoredRoute(ignoredViews, to, from)) {
      return;
    }
    const name = trackNameFromRoute(to);
    if (isNavigationFailure(failure, NavigationFailureType.aborted)) {
      if (debugEnabled) {
        console.log(`[Segment]: '${name}' not tracked due to navigation aborted`);
      }
    } else if (isNavigationFailure(failure, NavigationFailureType.cancelled)) {
      if (debugEnabled) {
        console.log(`[Segment]: '${name}' not tracked due to navigation cancelled`);
      }
    }
    const additionalData = deriveAdditionalEventData ? await deriveAdditionalEventData(to, from) : {};
    const additionalEventData = {
      ...additionalData,
      ...to.meta?.segmentAdditionalEventData
    };
    const fullToUrl = fullUrlFromRoute(to, baseUrl);
    const fullFromUrl = fullUrlFromRoute(from, baseUrl);
    const properties = {
      path: fullToUrl,
      title: to.name,
      url: fullToUrl,
      referrer: fullFromUrl,
      ...additionalEventData
    };
    trackRoute("screenViewed", name, properties);
  });
}
async function trackRoute(category, name, properties) {
  const { page: trackPage } = useSegment(segment);
  if (trackOnNextTick) {
    void nextTick(() => {
      trackPage("screenViewed", name, properties);
    });
  } else {
    await trackPage("screenViewed", name, properties);
  }
}
function trackNameFromRoute(to) {
  const name = to.meta && typeof to.meta.segment === "string" && !!to.meta.segment ? to.meta.segment : to.name;
  return name ?? to.path;
}
